import { forwardRef, useEffect, useRef } from 'react';
import clsx from 'clsx';

import Icon from '@components/icon/Icon';
import { CheckboxProps } from 'types/checkbox';

import theme from '../../../themes/colors';

import './style.css';

export const CHECKBOX_VARIANTS = {
  default: 'default',
  v2: 'v2'
} as const;

const defaultInputStyles =
  'appearance-none border cursor-pointer rounded-md border-neutral checked:border-primary focus:ring-transparent disabled:cursor-default checkbox size-4.5';

const getCheckboxIconFill = (disabled?: boolean, isChecked?: boolean) => {
  if (disabled && isChecked) return theme.cofeeNeutral6;
  if (disabled) return theme.grayDisabled;
  return theme.primary;
};

const getInputClassName = ({
  variant,
  isChecked,
  isIndeterminate,
  disabled,
  inputUnCheckedStyle
}: {
  variant: NonNullable<CheckboxProps['variant']>;
  isChecked?: boolean;
  isIndeterminate?: boolean;
  disabled?: boolean;
  inputUnCheckedStyle?: string;
}) => {
  const isActive = Boolean(isChecked || isIndeterminate);

  if (variant === 'v2') {
    return clsx(
      'checkbox-v2 size-4.5 cursor-pointer appearance-none rounded border border-cofeeNeutral6 focus:ring-transparent disabled:cursor-default',
      disabled && '!border-cofeeNeutral6',
      isActive
        ? 'border-cofeePrimary6 bg-cofeePrimary6'
        : clsx('!border-cofeeNeutral6 bg-cofeeNeutral1', inputUnCheckedStyle),
      disabled && isActive && '!border-cofeeNeutral6 !bg-cofeeNeutral6'
    );
  }

  return clsx(
    defaultInputStyles,
    !isActive && inputUnCheckedStyle,
    disabled && !isActive && '!border-grayDisabled',
    disabled && isActive && '!border-cofeeNeutral6 !bg-cofeeNeutral6'
  );
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      handleChange,
      checkboxStyle,
      labelStyle,
      disabled,
      className,
      htmlKey,
      isChecked,
      isIndeterminate = false,
      checkIconUncheckedStyle,
      inputUnCheckedStyle,
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const localRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (localRef.current) {
        localRef.current.indeterminate = Boolean(isIndeterminate);
      }
    }, [isIndeterminate]);

    const setRefs = (node: HTMLInputElement | null) => {
      localRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        // eslint-disable-next-line no-param-reassign
        ref.current = node;
      }
    };

    const isActive = Boolean(isChecked || isIndeterminate);

    return (
      <div
        className={`flex w-fit flex-row items-center ${className || ''} `}
        onClick={(e) => {
          e.stopPropagation();
        }}
        role="presentation"
      >
        <label
          className={`relative flex items-center ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer '
          } ${checkboxStyle}`}
        >
          <input
            className={getInputClassName({
              variant,
              isChecked,
              isIndeterminate,
              disabled,
              inputUnCheckedStyle
            })}
            type="checkbox"
            ref={setRefs}
            onChange={(e) => {
              handleChange(e);
            }}
            id={htmlKey || (label as string)}
            disabled={disabled}
            checked={isChecked}
            {...props}
          />
          {variant === 'v2' ? (
            <Icon
              name={isIndeterminate ? 'minus' : 'custom-color-check'}
              size="12"
              className={clsx(
                'checkbox-icon-v2 pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-cofeeNeutral1',
                isActive
                  ? 'opacity-100'
                  : `opacity-0 ${checkIconUncheckedStyle}`
              )}
              stroke={theme.cofeeNeutral1}
            />
          ) : (
            <Icon
              name="checkbox"
              fill={getCheckboxIconFill(disabled, isChecked)}
              size="none"
              className={`checkbox-icon absolute size-4.5 rounded-sm ${
                isChecked
                  ? 'opacity-100'
                  : `opacity-0 ${checkIconUncheckedStyle}`
              }`}
            />
          )}
        </label>
        {label != null && label !== '' && (
          <label
            htmlFor={htmlKey || (label as string)}
            className={`${labelStyle} ml-2 text-sm font-normal text-grey`}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

export default Checkbox;
